import { useState, useEffect, useRef } from 'react';
import {
  Box,
  FormControl,
  InputAdornment,
  OutlinedInput,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  CircularProgress,
  IconButton
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { SearchOutlined, CloseOutlined } from '@ant-design/icons'; // Added Close Icon
import useAuth from 'hooks/useAuth';
import { useAccountClient, useAnalyticsClient } from 'client/client-hook';
import { ListTradesReply } from 'proto/analytics-manager_pb';
import { ListCopiedTradesReq, ListCopiedTradesReply, AccountListReply } from 'proto/account-manager_pb';
import { useNavigate } from 'react-router-dom';
import { useDebouncedSearch } from 'hooks/useDebouncedSearch';

const platformNameMap: Record<string, string> = {
  CTR: 'cTrader',
  DXT: 'DXtrade',
  TL: 'TradeLocker',
  'MT-4': 'MetaTrader 4',
  'MT-5': 'MetaTrader 5',
  MTR: 'MatchTrader',
  TV: 'Tradovate'
};

const statusDisplayMap: Record<string, string> = {
  connected: 'Connected',
  disconnected: 'Disconnected',
  connection_error: 'Connection Error'
};

interface SearchResult {
  type: 'account' | 'trade' | 'copier-log';
  id: string;
  primaryText: string;
  secondaryText?: string;
  platform?: string;
  status?: string;
  statusColor?: string;
  tradeType?: string;
  tradeTypeColor?: string;
  accountId?: string;
  ticket?: string;
  navigateTo?: string;
}

interface GroupedResults {
  accounts: SearchResult[];
  trades: SearchResult[];
  copierLogs: SearchResult[];
}

const GlobalSearch = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const getAccountClient = useAccountClient();
  const getAnalyticsClient = useAnalyticsClient();

  const [searchValue, setSearchValue] = useState('');
  const [results, setResults] = useState<GroupedResults>({ accounts: [], trades: [], copierLogs: [] });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  const debouncedSearchValue = useDebouncedSearch(searchValue, 500, 2);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchAccounts = async (query: string): Promise<SearchResult[]> => {
    if (!user?.tcId) return [];

    try {
      const client = await getAccountClient();
      // Typed by getAccountList's own parameter (AsObject minus the optional
      // platform-filter fields AI Connect added).
      const req = {
        userId: user.tcId,
        pageSize: 5,
        paginationCursor: '',
        searchPattern: query
      };

      const res = (await client.getAccountList(req)) as AccountListReply.AsObject;
      return res.accountsList.map((account) => {
        const displayName = account.nickname || account.name?.value || account.accountNumber;
        const platformName = platformNameMap[account.platform] || account.platform;
        const statusDisplayName = statusDisplayMap[account.status.toLowerCase()] || account.status;
        // Determine status color
        let statusColor = 'text.secondary';
        if (account.status.toLowerCase() === 'connected') {
          statusColor = 'success.main';
        } else if (account.status.toLowerCase().includes('error')) {
          statusColor = 'error.main';
        } else if (account.status.toLowerCase() === 'disconnected') {
          statusColor = 'warning.main';
        }

        return {
          type: 'account' as const,
          id: account.accountId,
          primaryText: `${account.accountNumber} - ${displayName}`,
          platform: platformName,
          status: statusDisplayName,
          statusColor,
          accountId: account.accountId,
          navigateTo: `/accounts?search=${account.accountNumber}`
        };
      });
    } catch (error) {
      console.error('Error searching accounts:', error);
      return [];
    }
  };

  const searchTrades = async (query: string): Promise<SearchResult[]> => {
    if (!user?.tcId) return [];

    try {
      const client = await getAnalyticsClient();

      // Search both open and closed positions
      const [openRes, closedRes] = await Promise.all([
        client.listTrades({
          userId: user.tcId,
          pageSize: 3,
          paginationCursor: '',
          status: 1, // OPEN
          searchKey: query,
          accountId: ''
        }),
        client.listTrades({
          userId: user.tcId,
          pageSize: 3,
          paginationCursor: '',
          status: 2, // CLOSED
          searchKey: query,
          accountId: ''
        })
      ]);

      const openTrades = (openRes as ListTradesReply.AsObject).tradesList || [];
      const closedTrades = (closedRes as ListTradesReply.AsObject).tradesList || [];

      // Filter trades to only match by ticket number, not account number
      const filteredOpenTrades = openTrades.filter((trade) => trade.ticket?.toString().includes(query));
      const filteredClosedTrades = closedTrades.filter((trade) => trade.ticket?.toString().includes(query));

      // Map trades with their status preserved
      const openTradesResults = filteredOpenTrades.map((trade) => {
        const tradeTypeColor = trade.type?.toLowerCase().includes('buy') ? 'success.main' : 'error.main';
        return {
          type: 'trade' as const,
          id: trade.ticket?.toString() || '',
          primaryText: `Trade #${trade.ticket} - ${trade.symbol}`,
          secondaryText: `${trade.accountNumber} • ${trade.lots} lots`,
          tradeType: trade.type,
          tradeTypeColor,
          ticket: trade.ticket?.toString(),
          accountId: trade.accountId,
          status: 'open',
          navigateTo: `/account-trades?accountId=${trade.accountId}&ticket=${trade.ticket}&status=open`
        };
      });

      const closedTradesResults = filteredClosedTrades.map((trade) => {
        const tradeTypeColor = trade.type?.toLowerCase().includes('buy') ? 'success.main' : 'error.main';
        return {
          type: 'trade' as const,
          id: trade.ticket?.toString() || '',
          primaryText: `Trade #${trade.ticket} - ${trade.symbol}`,
          secondaryText: `${trade.accountNumber} • ${trade.lots} lots`,
          tradeType: trade.type,
          tradeTypeColor,
          ticket: trade.ticket?.toString(),
          accountId: trade.accountId,
          status: 'closed',
          navigateTo: `/account-trades?accountId=${trade.accountId}&ticket=${trade.ticket}&status=closed`
        };
      });

      const allTrades = [...openTradesResults, ...closedTradesResults].slice(0, 5);

      return allTrades;
    } catch (error) {
      console.error('Error searching trades:', error);
      return [];
    }
  };

  const searchCopierLogs = async (query: string): Promise<SearchResult[]> => {
    if (!user?.tcId) return [];

    try {
      const client = await getAccountClient();
      const req: ListCopiedTradesReq.AsObject = {
        userId: user.tcId,
        pageSize: 5,
        paginationCursor: '',
        searchTicket: {
          value: query
        }
      };

      const res = (await client.listCopiedTrades(req)) as ListCopiedTradesReply.AsObject;

      // Filter copier logs to match by either master ticket OR slave ticket
      return res.tradesList
        .filter((trade) => {
          const masterTicket = trade.master?.ticket || '';
          const slaveTicket = trade.ticket || '';
          return masterTicket.toString().includes(query) || slaveTicket.toString().includes(query);
        })
        .map((trade) => {
          const masterTicket = trade.master?.ticket || 'N/A';
          const masterAccountNumber = trade.master?.accountNumber || 'Unknown';
          const slaveAccountNumber = trade.accountNumber || 'Unknown';
          return {
            type: 'copier-log' as const,
            id: masterTicket,
            primaryText: `Copier Log #${masterTicket}`,
            secondaryText: `Master: ${masterAccountNumber} → Slave: ${slaveAccountNumber}`,
            ticket: masterTicket,
            navigateTo: `/trade-copier/copier-logs?ticket=${masterTicket}`
          };
        });
    } catch (error) {
      console.error('Error searching copier logs:', error);
      return [];
    }
  };

  useEffect(() => {
    const performSearch = async () => {
      if (!debouncedSearchValue.trim() || debouncedSearchValue.length < 2) {
        setResults({ accounts: [], trades: [], copierLogs: [] });
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const [accounts, trades, copierLogs] = await Promise.all([
          searchAccounts(debouncedSearchValue),
          searchTrades(debouncedSearchValue),
          searchCopierLogs(debouncedSearchValue)
        ]);
        setResults({ accounts, trades, copierLogs });
      } catch (error) {
        console.error('Search error:', error);
        setResults({ accounts: [], trades: [], copierLogs: [] });
      } finally {
        setLoading(false);
      }
    };

    performSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchValue]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchValue(value);
    setOpen(value.length >= 2);

    // Set loading immediately when user types (before debounce)
    if (value.length >= 2) {
      setLoading(true);
    }
  };

  const handleResultClick = (result: SearchResult) => {
    if (result.navigateTo) {
      navigate(result.navigateTo);
    }
    setOpen(false);
    setSearchValue('');
  };

  const totalResults = results.accounts.length + results.trades.length + results.copierLogs.length;
  const hasResults = totalResults > 0;

  return (
    <Box sx={{ ml: { xs: 0, md: 1 }, position: 'relative', display: 'inline-block' }} ref={searchRef}>
      <FormControl sx={{ width: { xs: '100%', md: 250 } }}>
        <OutlinedInput
          size="small"
          id="global-search"
          value={searchValue}
          onChange={handleInputChange}
          onFocus={() => searchValue.length >= 2 && setOpen(true)}
          startAdornment={
            <InputAdornment position="start" sx={{ mr: -1 }}>
              <SearchOutlined style={{ fontSize: '1.25rem', fontWeight: 'bold', strokeWidth: 50, color: theme.palette.text.primary }} />
            </InputAdornment>
          }
          endAdornment={
            searchValue ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSearchValue('');
                    setOpen(false);
                    setResults({ accounts: [], trades: [], copierLogs: [] });
                  }}
                  edge="end"
                >
                  <CloseOutlined style={{ fontSize: '0.875rem', color: theme.palette.text.secondary }} />
                </IconButton>
              </InputAdornment>
            ) : null
          }
          placeholder="Search"
          sx={{
            fontSize: '1rem',
            '& input': {
              padding: '8px 16px'
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.border.main,
              borderRadius: '10px'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
              boxShadow: 'none'
            },
            '&.Mui-focused': {
              boxShadow: 'none'
            }
          }}
        />
      </FormControl>

      {/* Search Results Dropdown */}
      {open && searchValue.length >= 2 && (
        <Paper
          sx={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: 0,
            width: { xs: '100%', md: 450 },
            maxHeight: '70vh',
            overflowY: 'auto',
            zIndex: 1300,
            boxShadow: theme.shadows[8],
            borderRadius: '10px',
            border: `1px solid ${theme.palette.border.main}`
          }}
        >
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress size={30} />
            </Box>
          )}

          {!loading && !hasResults && (
            <Box sx={{ py: 3, px: 2, textAlign: 'center' }}>
              <Typography color="text.secondary">No results found for "{searchValue}"</Typography>
            </Box>
          )}

          {!loading && hasResults && (
            <List sx={{ py: 1 }}>
              {/* Trading Accounts Section */}
              {results.accounts.length > 0 && (
                <>
                  <ListItem sx={{ py: 0.5, px: 2 }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" textTransform="uppercase">
                      Trading Accounts
                    </Typography>
                  </ListItem>
                  {results.accounts.map((account, index) => (
                    <ListItemButton
                      key={`account-${index}`}
                      onClick={() => handleResultClick(account)}
                      sx={{
                        px: 2,
                        py: 1,
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover
                        }
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={500}>
                            {account.primaryText}
                          </Typography>
                        }
                        secondary={
                          <Box component="span">
                            <Typography component="span" variant="caption" color="text.secondary">
                              {account.platform}
                            </Typography>
                            {account.platform && account.status && (
                              <Typography component="span" variant="caption" color="text.secondary">
                                {' • '}
                              </Typography>
                            )}
                            <Typography component="span" variant="caption" color={account.statusColor || 'text.secondary'}>
                              {account.status}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  ))}
                  {(results.trades.length > 0 || results.copierLogs.length > 0) && <Divider sx={{ my: 1 }} />}
                </>
              )}

              {/* Trades Section */}
              {results.trades.length > 0 && (
                <>
                  <ListItem sx={{ py: 0.5, px: 2 }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" textTransform="uppercase">
                      Trades
                    </Typography>
                  </ListItem>
                  {results.trades.map((trade, index) => (
                    <ListItemButton
                      key={`trade-${index}`}
                      onClick={() => handleResultClick(trade)}
                      sx={{
                        px: 2,
                        py: 1,
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover
                        }
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={500}>
                            {trade.primaryText}
                          </Typography>
                        }
                        secondary={
                          <Box component="span">
                            <Typography component="span" variant="caption" color="text.secondary">
                              {trade.secondaryText?.split(' • ')[0]}
                            </Typography>
                            <Typography component="span" variant="caption" color="text.secondary">
                              {' • '}
                            </Typography>
                            <Typography component="span" variant="caption" color={trade.tradeTypeColor || 'text.secondary'}>
                              {trade.tradeType}
                            </Typography>
                            <Typography component="span" variant="caption" color="text.secondary">
                              {' • '}
                            </Typography>
                            <Typography component="span" variant="caption" color="text.secondary">
                              {trade.secondaryText?.split(' • ')[1]}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  ))}
                  {results.copierLogs.length > 0 && <Divider sx={{ my: 1 }} />}
                </>
              )}

              {/* Copier Logs Section */}
              {results.copierLogs.length > 0 && (
                <>
                  <ListItem sx={{ py: 0.5, px: 2 }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" textTransform="uppercase">
                      Copier Logs
                    </Typography>
                  </ListItem>
                  {results.copierLogs.map((log, index) => (
                    <ListItemButton
                      key={`copier-${index}`}
                      onClick={() => handleResultClick(log)}
                      sx={{
                        px: 2,
                        py: 1,
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover
                        }
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={500}>
                            {log.primaryText}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {log.secondaryText}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  ))}
                </>
              )}
            </List>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default GlobalSearch;
